// Security tab — manage roles + granular permissions.
//
// Lives inside the company Settings phase (admin-gated). Backed by
// /api/permissions/* on the backend (see routes/permissions.js).
//
// Three sections:
//   1. Members list — every active member, role pill, "Manage" button
//   2. Permission editor modal — opens from a member row, shows the
//      catalogued permissions grouped by category as checkboxes, plus
//      a lock toggle (only Global Admin sees the lock toggle when
//      editing an Admin)
//   3. Global Admin transfer — current GA + a Transfer button that
//      opens a "pick + confirm" two-step modal
//
// Self-edit (a member opening their own row) shows the same modal but
// with locked permissions filtered out by the backend (so they can't
// see what GA pinned). Backend already enforces; this UI just renders
// what comes back.

import { useEffect, useState, useCallback, useMemo } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

function authedFetch(url, authToken, opts = {}) {
  return fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
      ...(opts.headers || {}),
    },
  });
}

// Category headers + ordering for the permission grid.
// Falls back to the slug's category prefix if a category isn't listed.
const CATEGORY_LABELS = {
  brand: 'Brand & content',
  team: 'Team management',
  billing: 'Billing',
  integrations: 'Integrations',
  demos: 'Recordings & demos',
  spaces: 'Buyer Spaces',
  voice: 'Voice & AI',
  sharing: 'Sharing',
  security: 'Security',
};

const CATEGORY_ORDER = ['brand', 'team', 'billing', 'integrations', 'demos', 'spaces', 'voice', 'sharing', 'security'];

function roleBadgeClass(role) {
  if (role === 'global_admin') return 'role-badge role-global-admin';
  if (role === 'admin') return 'role-badge role-admin';
  return 'role-badge role-member';
}

function roleLabel(role) {
  if (role === 'global_admin') return 'Global Admin';
  if (role === 'admin') return 'Admin';
  return 'Member';
}

export default function SecurityTab({ authToken, currentUser, showToast }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingUserId, setEditingUserId] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);

  const isGlobalAdmin = !!(currentUser && currentUser.company && currentUser.company.isGlobalAdmin);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authedFetch(`${API_BASE}/api/permissions/users`, authToken);
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Failed to load members (${res.status})`);
      }
      const data = await res.json();
      setMembers(data.members || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const globalAdmin = useMemo(() => members.find((m) => m.role === 'global_admin') || null, [members]);
  const transferableMembers = useMemo(
    () => members.filter((m) => m.role !== 'global_admin' && !m.isSelf),
    [members]
  );

  if (loading) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ color: 'var(--text-secondary)' }}>Loading members…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{
          padding: 12, borderRadius: 8,
          background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626',
        }}>
          {error}
          <button
            type="button"
            onClick={loadMembers}
            style={{ marginLeft: 12, padding: '4px 12px', borderRadius: 6, border: '1px solid currentColor', background: 'transparent', color: 'inherit', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0 32px' }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
          Roles & permissions
        </h3>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          Three roles: Global Admin (one per company, always all permissions, immutable),
          Admin (defaults to all permissions; Global Admin can revoke + lock individual ones),
          Member (curated default-on set; Admins can grant extras). Click any member to manage
          their permissions.
        </p>
      </div>

      {/* Global Admin block */}
      <div style={{
        padding: 16, marginBottom: 24,
        borderRadius: 12,
        background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.06), rgba(79, 70, 229, 0.06))',
        border: '1px solid rgba(124, 58, 237, 0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 240 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6d28d9', marginBottom: 4 }}>
              Global Admin
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', textAlign: 'left' }}>
              {globalAdmin
                ? (globalAdmin.fullName || globalAdmin.email)
                : 'No Global Admin assigned (this should not happen — contact support)'}
            </div>
            {globalAdmin && globalAdmin.email && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'left' }}>
                {globalAdmin.email}
                {globalAdmin.isSelf && <span style={{ marginLeft: 6, opacity: 0.7 }}>(you)</span>}
              </div>
            )}
          </div>
          {isGlobalAdmin && (
            <button
              type="button"
              onClick={() => setTransferOpen(true)}
              disabled={transferableMembers.length === 0}
              title={transferableMembers.length === 0
                ? 'No teammates yet — invite someone first, then you can transfer Global Admin to them.'
                : 'Pick a teammate to take over as Global Admin.'}
              style={{
                padding: '8px 16px', borderRadius: 8,
                border: '1px solid rgba(124, 58, 237, 0.4)',
                background: 'white',
                color: '#6d28d9',
                cursor: transferableMembers.length === 0 ? 'not-allowed' : 'pointer',
                opacity: transferableMembers.length === 0 ? 0.5 : 1,
                fontSize: 13, fontWeight: 600,
              }}
            >
              Transfer Global Admin
            </button>
          )}
          {!isGlobalAdmin && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 240, textAlign: 'right' }}>
              Only the current Global Admin can transfer this role.
            </div>
          )}
        </div>
      </div>

      {/* Members list */}
      <div style={{
        borderRadius: 12, border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 16px',
          background: 'rgba(var(--on-bg, 0,0,0), 0.04)',
          borderBottom: '1px solid var(--border-color, rgba(0,0,0,0.08))',
          fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)',
        }}>
          Members ({members.length})
        </div>
        {members.map((m) => (
          <MemberRow
            key={m.id}
            member={m}
            currentUserIsGlobalAdmin={isGlobalAdmin}
            onEdit={() => setEditingUserId(m.id)}
          />
        ))}
      </div>

      {/* Per-user permission editor modal */}
      {editingUserId && (
        <PermissionEditorModal
          authToken={authToken}
          userId={editingUserId}
          isCurrentUserGlobalAdmin={isGlobalAdmin}
          onClose={() => setEditingUserId(null)}
          onSaved={() => {
            setEditingUserId(null);
            // Refresh members in case the role changed.
            loadMembers();
          }}
          showToast={showToast}
        />
      )}

      {/* Global Admin transfer modal */}
      {transferOpen && (
        <TransferGlobalAdminModal
          authToken={authToken}
          candidates={transferableMembers}
          onClose={() => setTransferOpen(false)}
          onSuccess={() => {
            setTransferOpen(false);
            loadMembers();
            if (showToast) showToast('Global Admin transferred. You are now an Admin.');
          }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MemberRow — one row in the members list
// ─────────────────────────────────────────────────────────────────

function MemberRow({ member, currentUserIsGlobalAdmin, onEdit }) {
  const isGA = member.role === 'global_admin';
  // Anyone can edit their own permissions (subject to lock filtering on
  // the backend). Only Global Admin can edit other people's permissions.
  // Global Admin's permissions are immutable — clicking their row is a
  // no-op except to view (we still let people open it to see).
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 16, padding: '12px 16px',
      borderBottom: '1px solid var(--border-color, rgba(0,0,0,0.06))',
      textAlign: 'left',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            {member.fullName || member.email}
          </span>
          {member.isSelf && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>(you)</span>
          )}
          <span className={roleBadgeClass(member.role)}>{roleLabel(member.role)}</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
          {member.email}
        </div>
      </div>
      <button
        type="button"
        onClick={onEdit}
        disabled={isGA && !member.isSelf}
        title={isGA && !member.isSelf
          ? 'Global Admin permissions are immutable. To change who is Global Admin, use Transfer Global Admin.'
          : ''}
        style={{
          padding: '8px 14px', borderRadius: 8,
          border: '1px solid var(--border-color, rgba(0,0,0,0.15))',
          background: 'white', color: 'var(--text-primary)',
          cursor: isGA && !member.isSelf ? 'not-allowed' : 'pointer',
          opacity: isGA && !member.isSelf ? 0.4 : 1,
          fontSize: 13, fontWeight: 500,
        }}
      >
        {member.isSelf ? 'My permissions' : 'Manage'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PermissionEditorModal — categorised checkbox grid + lock toggles
// Exported so the legacy Security & Permissions page can use it
// without duplicating the permission catalogue logic.
// ─────────────────────────────────────────────────────────────────

export function PermissionEditorModal({ authToken, userId, isCurrentUserGlobalAdmin, onClose, onSaved, showToast }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matrix, setMatrix] = useState([]); // [{ slug, category, displayName, description, granted, source, locked, overrideExists }]
  const [target, setTarget] = useState(null); // { userId, role, isSelf }
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({}); // slug -> { granted, locked }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPendingChanges({});
    authedFetch(`${API_BASE}/api/permissions/users/${userId}`, authToken)
      .then(async (r) => {
        if (!r.ok) {
          const p = await r.json().catch(() => ({}));
          throw new Error(p.error || `Failed (${r.status})`);
        }
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setMatrix(data.permissions || []);
        setTarget({ userId: data.userId, role: data.role, isSelf: data.isSelf });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [authToken, userId]);

  // Group rows by category preserving CATEGORY_ORDER.
  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const p of matrix) {
      if (!buckets.has(p.category)) buckets.set(p.category, []);
      buckets.get(p.category).push(p);
    }
    const ordered = [];
    for (const cat of CATEGORY_ORDER) {
      if (buckets.has(cat)) ordered.push([cat, buckets.get(cat)]);
    }
    // Catch any unknown categories at the end.
    for (const [cat, rows] of buckets.entries()) {
      if (!CATEGORY_ORDER.includes(cat)) ordered.push([cat, rows]);
    }
    return ordered;
  }, [matrix]);

  const targetIsGA = target && target.role === 'global_admin';
  const canEditAtAll = !targetIsGA;
  // Lock toggle is only meaningful when Global Admin edits an Admin
  // (not a Member — Members don't need locking; Admin defaults to
  // everything-on and locking pins specific revocations).
  const showLockColumn = isCurrentUserGlobalAdmin && target && target.role === 'admin' && !target.isSelf;

  const onTogglePermission = (slug, current, currentlyLocked) => {
    if (!canEditAtAll) return;
    setPendingChanges((prev) => {
      const next = { ...prev };
      const newGranted = !(prev[slug] ? prev[slug].granted : current);
      next[slug] = {
        granted: newGranted,
        locked: prev[slug] ? prev[slug].locked : !!currentlyLocked,
      };
      return next;
    });
  };

  const onToggleLock = (slug, currentGranted, currentLocked) => {
    if (!showLockColumn) return;
    setPendingChanges((prev) => {
      const next = { ...prev };
      const newLocked = !(prev[slug] ? prev[slug].locked : currentLocked);
      next[slug] = {
        granted: prev[slug] ? prev[slug].granted : currentGranted,
        locked: newLocked,
      };
      return next;
    });
  };

  const dirty = Object.keys(pendingChanges).length > 0;

  const onSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const changes = Object.entries(pendingChanges).map(([slug, v]) => ({
        slug,
        granted: !!v.granted,
        locked: !!v.locked,
      }));
      const res = await authedFetch(`${API_BASE}/api/permissions/users/${userId}`, authToken, {
        method: 'PATCH',
        body: JSON.stringify({ changes }),
      });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.error || `Save failed (${res.status})`);
      }
      const data = await res.json();
      const failed = (data.results || []).filter((r) => r.error);
      if (failed.length > 0) {
        if (showToast) {
          showToast(`Saved ${changes.length - failed.length}/${changes.length}. Some changes were blocked: ${failed[0].error}`);
        }
      } else if (showToast) {
        showToast(`Updated ${changes.length} permission${changes.length === 1 ? '' : 's'}.`);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 720, maxHeight: '90vh',
          background: 'white', color: 'var(--text-primary)',
          borderRadius: 14, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color, rgba(0,0,0,0.08))' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, textAlign: 'left' }}>
                {target && target.isSelf ? 'My permissions' : 'Manage permissions'}
              </h3>
              {target && (
                <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-secondary)', textAlign: 'left' }}>
                  Role: <span className={roleBadgeClass(target.role)} style={{ marginLeft: 4 }}>{roleLabel(target.role)}</span>
                  {targetIsGA && <span style={{ marginLeft: 8 }}>— immutable</span>}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                width: 32, height: 32, border: 'none', background: 'transparent',
                cursor: 'pointer', fontSize: 22, lineHeight: 1, color: 'var(--text-secondary)',
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
          {loading && <p style={{ padding: '24px 0', color: 'var(--text-secondary)' }}>Loading…</p>}
          {error && (
            <div style={{ padding: 16, marginTop: 16, borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626' }}>
              {error}
            </div>
          )}
          {!loading && !error && targetIsGA && (
            <p style={{ padding: '24px 0', color: 'var(--text-secondary)', fontSize: 14 }}>
              Global Admin permissions are immutable — every capability is granted and cannot be revoked. To change who is Global Admin, close this dialog and use Transfer Global Admin.
            </p>
          )}
          {!loading && !error && !targetIsGA && (
            <div style={{ paddingBottom: 16 }}>
              {grouped.map(([category, rows]) => (
                <div key={category} style={{ marginTop: 24 }}>
                  <h4 style={{
                    margin: '0 0 8px', fontSize: 12, fontWeight: 700,
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    textAlign: 'left',
                  }}>
                    {CATEGORY_LABELS[category] || category}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {rows.map((p) => {
                      const pendingForSlug = pendingChanges[p.slug];
                      const effectiveGranted = pendingForSlug ? pendingForSlug.granted : p.granted;
                      const effectiveLocked = pendingForSlug ? pendingForSlug.locked : p.locked;
                      // A row is read-only when:
                      //   - Target is GA (handled above)
                      //   - Target is self AND row is locked (backend already
                      //     filtered, but defense-in-depth)
                      //   - Caller is not Global Admin AND row is locked
                      //     by GA — only GA can flip a locked row
                      const lockedByGA = p.locked && !isCurrentUserGlobalAdmin;
                      const readOnly = lockedByGA;
                      return (
                        <div
                          key={p.slug}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 12,
                            padding: '10px 12px', borderRadius: 8,
                            background: pendingForSlug ? 'rgba(124, 58, 237, 0.05)' : 'transparent',
                            opacity: readOnly ? 0.6 : 1,
                            textAlign: 'left',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={effectiveGranted}
                            disabled={readOnly}
                            onChange={() => onTogglePermission(p.slug, p.granted, p.locked)}
                            style={{ marginTop: 2, cursor: readOnly ? 'not-allowed' : 'pointer' }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                              {p.displayName}
                              {p.locked && (
                                <span title="Locked by Global Admin" style={{ marginLeft: 8, fontSize: 12, color: '#6d28d9' }}>
                                  🔒 Locked
                                </span>
                              )}
                              {pendingForSlug && (
                                <span style={{ marginLeft: 8, fontSize: 11, color: '#6d28d9', fontWeight: 700 }}>
                                  CHANGED
                                </span>
                              )}
                            </div>
                            {p.description && (
                              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                                {p.description}
                              </div>
                            )}
                          </div>
                          {showLockColumn && (
                            <label
                              title="Lock this permission so the affected admin can't see or change it"
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 12, color: 'var(--text-secondary)',
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={effectiveLocked}
                                onChange={() => onToggleLock(p.slug, p.granted, p.locked)}
                              />
                              Lock
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid var(--border-color, rgba(0,0,0,0.08))',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {dirty
              ? `${Object.keys(pendingChanges).length} pending change${Object.keys(pendingChanges).length === 1 ? '' : 's'}`
              : (canEditAtAll ? 'Toggle a checkbox to make changes' : '')}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 14px', borderRadius: 8,
                border: '1px solid var(--border-color, rgba(0,0,0,0.15))',
                background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 500,
              }}
            >
              {dirty ? 'Discard' : 'Close'}
            </button>
            {canEditAtAll && (
              <button
                type="button"
                onClick={onSave}
                disabled={!dirty || saving}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: !dirty || saving ? 'rgba(124, 58, 237, 0.4)' : '#6d28d9',
                  color: 'white', cursor: !dirty || saving ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600,
                }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// TransferGlobalAdminModal — pick + confirm
// Exported so the legacy Security & Permissions page can use it.
// ─────────────────────────────────────────────────────────────────

export function TransferGlobalAdminModal({ authToken, candidates, onClose, onSuccess, showToast }) {
  const [selectedId, setSelectedId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const selected = candidates.find((c) => c.id === Number(selectedId));

  const onSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await authedFetch(`${API_BASE}/api/permissions/transfer-global`, authToken, {
        method: 'POST',
        body: JSON.stringify({ toUserId: selected.id }),
      });
      if (!res.ok) {
        const p = await res.json().catch(() => ({}));
        throw new Error(p.error || `Transfer failed (${res.status})`);
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          background: 'white', color: 'var(--text-primary)',
          borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color, rgba(0,0,0,0.08))' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, textAlign: 'left' }}>
            Transfer Global Admin
          </h3>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'left', lineHeight: 1.5 }}>
            Pick a teammate to take over as Global Admin. You'll keep your account but drop down to Admin —
            you can be promoted again later.
          </p>
        </div>
        <div style={{ padding: 24 }}>
          {!confirming ? (
            <>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, textAlign: 'left' }}>
                New Global Admin
              </label>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px',
                  borderRadius: 8, border: '1px solid var(--border-color, rgba(0,0,0,0.15))',
                  fontSize: 14, background: 'white', fontFamily: 'inherit',
                }}
              >
                <option value="">Select a teammate…</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.fullName || c.email)} ({c.role === 'admin' ? 'Admin' : 'Member'})
                  </option>
                ))}
              </select>
            </>
          ) : (
            <div style={{ padding: 16, borderRadius: 8, background: 'rgba(124, 58, 237, 0.06)', border: '1px solid rgba(124, 58, 237, 0.25)' }}>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, textAlign: 'left' }}>
                Confirm: <strong>{selected ? (selected.fullName || selected.email) : ''}</strong> will become the new Global Admin.
                This is immediate. Your account drops to Admin.
              </p>
            </div>
          )}
          {error && (
            <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>
        <div style={{
          padding: '14px 24px', borderTop: '1px solid var(--border-color, rgba(0,0,0,0.08))',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button
            type="button"
            onClick={confirming ? () => setConfirming(false) : onClose}
            disabled={submitting}
            style={{
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid var(--border-color, rgba(0,0,0,0.15))',
              background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 500,
            }}
          >
            {confirming ? 'Back' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => (confirming ? onSubmit() : setConfirming(true))}
            disabled={!selected || submitting}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: !selected || submitting ? 'rgba(124, 58, 237, 0.4)' : '#6d28d9',
              color: 'white', cursor: !selected || submitting ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 600,
            }}
          >
            {submitting ? 'Transferring…' : confirming ? 'Confirm transfer' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
